import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { extractError } from "../lib/formatError";

const STATUSES = ["", "pending", "confirmed", "failed", "expired"];
const PROVIDERS = ["", "nowpayments"];

export default function AdminDeposits() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [provider, setProvider] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (status) params.set("status", status);
      if (provider) params.set("provider", provider);
      const { data } = await api.get(`/admin/deposits?${params}`);
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [status, provider]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-deposits">
      <div className="text-[10px] tracking-[0.4em] uppercase text-[color:var(--muted)] mb-2">Deposits</div>
      <h1 className="font-display italic text-4xl text-[color:var(--ink)] mb-6">All deposits ({total})</h1>

      <div className="flex gap-3 mb-5">
        <FilterSelect label="Status" value={status} setValue={setStatus} options={STATUSES} testid="filter-status" />
        <FilterSelect label="Provider" value={provider} setValue={setProvider} options={PROVIDERS} testid="filter-provider" />
        <button onClick={load} className="px-3 py-1.5 border border-[color:var(--line)] text-[10px] tracking-widest uppercase text-[color:var(--muted)] hover:text-[color:var(--gold)] hover:border-[color:var(--gold)]" data-testid="refresh-btn">Refresh</button>
      </div>

      {error && <div className="text-xs text-[color:var(--red)] border border-[color:var(--red)] px-3 py-2 mb-3">{error}</div>}
      {loading ? (
        <div className="text-[color:var(--muted)] text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-[color:var(--muted)] text-sm italic">No deposits.</div>
      ) : (
        <div className="border border-[color:var(--line)] overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-[color:var(--line)] text-[10px] tracking-[0.25em] uppercase text-[color:var(--muted)]">
              <tr>
                <Th>Created</Th><Th>Provider</Th><Th>Amount</Th><Th>Crypto</Th><Th>Status</Th><Th>Tx hash</Th><Th>Ref</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--line)]">
              {items.map((d) => (
                <tr key={d.id} data-testid={`deposit-${d.id}`} className="hover:bg-[color:var(--card)]">
                  <Td className="text-[color:var(--muted)]">{new Date(d.created_at).toLocaleString()}</Td>
                  <Td className="uppercase tracking-widest text-[10px]">{d.provider}</Td>
                  <Td className="font-mono text-[color:var(--gold)]">${d.amount_usd.toFixed(2)}</Td>
                  <Td className="font-mono text-[10px]">{d.crypto_amount ? `${d.crypto_amount} ${d.crypto_currency}` : "—"}</Td>
                  <Td><StatusPill s={d.status} /></Td>
                  <Td className="font-mono text-[10px] break-all max-w-[160px]">{d.tx_hash || "—"}</Td>
                  <Td className="font-mono text-[10px] text-[color:var(--muted)]">{d.id.slice(0, 8)}…</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, setValue, options, testid }) {
  return (
    <label className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-[color:var(--muted)]">
      {label}
      <select value={value} onChange={(e) => setValue(e.target.value)} className="bg-[color:var(--shell-2)] border border-[color:var(--line)] px-2 py-1 text-[color:var(--ink)]" data-testid={testid}>
        {options.map((o) => <option key={o} value={o}>{o || "all"}</option>)}
      </select>
    </label>
  );
}

function Th({ children }) { return <th className="text-left px-3 py-2 font-normal">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2 ${className}`}>{children}</td>; }
function StatusPill({ s }) {
  const map = { pending: "muted", confirmed: "gold", failed: "red", expired: "red", paid: "gold", rejected: "red", approved: "gold" };
  const c = map[s] || "muted";
  return <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 border" style={{ color: `var(--${c})`, borderColor: `var(--${c})` }}>{s}</span>;
}
